import { ApolloClient, ApolloLink, createHttpLink, gql, InMemoryCache, isApolloError, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { onError } from '@apollo/client/link/error';
import { getOperationAST } from 'graphql';
import { Kind, OperationTypeNode } from 'graphql/language';
import { createClient } from 'graphql-ws';
import Cookies from 'universal-cookie';
import io from 'socket.io-client';
import { createChatCommandLink } from './chatCommandLink';
import { chatMessageTypePolicy } from './chatSubmission';

import { reportFeLog } from './frontendLog';

import type { ProductListingConnection } from '../graphqls/dest/graphql';

import { expiredUser } from '../pages/users/partials/session';

const cookies = new Cookies();

const getToken = () => {
  const Retoken: string = cookies.get('token');
  return Retoken;
};

const link = createHttpLink({ uri: import.meta.env.VITE_GRAPHQL_URL });

let websocketRetryAttempt = 0;
let websocketConnectedBefore = false;
let connectionId: string | undefined;
let connectedAt: number | undefined;
let retryAttempt = 0;

function reportWs(eventName: string, metadata: Record<string, unknown> = {}) {
  void reportFeLog({
    source: 'apollo_subscription',
    log_type: 'WS_EVENT',
    event_name: eventName,
    operation_type: 'subscription',
    metadata: {
      connectionId,
      retryAttempt,
      online: navigator.onLine,
      visibilityState: document.visibilityState,
      ...metadata,
    },
  });
}

const wsClient = createClient({
  url: import.meta.env.VITE_SUBSCRIPTION_URL,
  lazy: true,
  retryAttempts: Infinity,
  connectionParams: () => {
    const token = getToken();
    if (!token) {
      void expiredUser();
    }

    return {
      authorization: token ? `Bearer ${token}` : '',
    };
  },
  shouldRetry: () => {
    websocketRetryAttempt += 1;
    console.log(`websocket retry attempt ${websocketRetryAttempt}`);
    return true;
  },
  on: {
    connecting: (isRetry) => {
      connectionId = crypto.randomUUID();
      connectedAt = undefined;
      retryAttempt = isRetry ? retryAttempt + 1 : 0;
      reportWs(isRetry ? 'websocket_retry' : 'websocket_connecting');
    },
    connected: (socket) => {
      websocketRetryAttempt = 0;
      const activeSocket = socket as WebSocket;
      console.log('websocket connected!!', { protocol: activeSocket.protocol || null });
      if (websocketConnectedBefore && import.meta.env.VITE_CHAT_COMMAND_SENDS_ENABLED === 'true') {
        window.dispatchEvent(new Event('forstok:chat-command-reconnected'));
      }
      websocketConnectedBefore = true;
      connectedAt = Date.now();
      reportWs('websocket_connected', { protocol: activeSocket.protocol });
      retryAttempt = 0;
    },
    closed: (event) => {
      const closeEvent = event as CloseEvent;
      console.log('websocket closed!!', {
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        willRetry: true,
      });
      reportWs('websocket_close', {
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
        durationMs: connectedAt == null ? undefined : Date.now() - connectedAt,
      });
      connectedAt = undefined;
    },
    error: (error) => {
      console.log('websocket error!!', error);
      reportWs('websocket_error', {
        message: error instanceof Error ? error.message : 'WebSocket transport error',
      });
    },
  },
});

const wsLink = new GraphQLWsLink(wsClient);

const commandLink = createChatCommandLink({
  enabled: import.meta.env.VITE_CHAT_COMMAND_SENDS_ENABLED === 'true',
  scope: () => {
    const user = cookies.get('users');
    if (!user?.profile_id || !user?.id) throw new Error('The signed-in account is unavailable');
    return `${user.profile_id}:${user.id}`;
  },
  storage: () => window.sessionStorage,
  requiresId: async (roomId, operation) => {
    const response = await client.query<{ chatRoom: { requiresClientRequestId?: boolean | null } }>({
      query: gql`query chatCommandPolicy($id: String!) { chatRoom(id: $id) { id requiresClientRequestId } }`,
      variables: { id: roomId },
      fetchPolicy: 'no-cache',
      context: operation.getContext(),
    });
    return response.data?.chatRoom?.requiresClientRequestId;
  },
});

const forstokHttpLink = ApolloLink.from([commandLink, link]);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION;
  },
  wsLink,
  forstokHttpLink,
);

const forstokAuthLink = setContext((_, { headers }) => {
  const token = getToken();
  if (!token) {
    void expiredUser();
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const errorLogLink = onError(({ graphQLErrors, networkError, operation }) => {
  const operationType = getOperationAST(operation.query, operation.operationName || undefined)?.operation;
  const common = {
    source: operationType === OperationTypeNode.SUBSCRIPTION ? 'apollo_subscription' : 'apollo_proxy',
    operation_name: operation.operationName || undefined,
    operation_type: operationType,
    variables: operation.variables,
  } as const;
  const metadata = operationType === OperationTypeNode.SUBSCRIPTION ? { connectionId } : {};

  // GraphQLWsLink wraps subscription operation errors in ApolloError.
  const subscriptionError =
    operationType === OperationTypeNode.SUBSCRIPTION && networkError && isApolloError(networkError)
      ? networkError
      : undefined;
  const errors = graphQLErrors ?? subscriptionError?.graphQLErrors ?? [];

  for (const error of errors) {
    const code = error.extensions?.code;
    if (code === 'UNAUTHENTICATED') {
      void expiredUser();
    }

    void reportFeLog({
      ...common,
      log_type: 'GRAPHQL_ERROR',
      error_code: typeof code === 'string' ? code : undefined,
      error_message: error.message,
      metadata: { ...metadata, path: error.path },
    });
  }

  const transportError = subscriptionError ? subscriptionError.networkError : networkError;
  if (transportError) {
    const status = 'statusCode' in transportError ? transportError.statusCode : undefined;
    void reportFeLog({
      ...common,
      log_type: 'NETWORK_ERROR',
      error_message: transportError.message,
      status_code: typeof status === 'number' && status >= 100 && status <= 599 ? status : undefined,
      metadata,
    });
  }
});

const evCompareCacheListing = (
  existing: ProductListingConnection,
  incoming: ProductListingConnection,
  args: any,
  readField: (fieldName: string, from?: any) => any,
) => {
  const readStringField = (fieldName: string, from?: unknown) => {
    const value = readField(fieldName, from);
    return typeof value === 'string' ? value : null;
  };

  const readArrayField = (fieldName: string, from?: unknown): unknown[] => {
    const value = readField(fieldName, from);
    return Array.isArray(value) ? value : [];
  };

  let result = true;
  // console.log(args, "args", existing, incoming)
  if (incoming?.edges && existing?.edges) {
    const incomingNodeMap = new Map<string, string[]>(
      incoming.edges.map((listingInc: any) => {
        const _listingInc = readField('node', listingInc);
        const _idInc = readStringField('id', _listingInc) ?? '';
        const _variantsInc = readArrayField('variants', _listingInc);
        const variantIds = _variantsInc
          .map((variant) => readStringField('id', variant))
          .filter((variantId): variantId is string => variantId !== null);

        return [_idInc, variantIds];
      }),
    );
    if (incomingNodeMap.size !== incoming.edges.length) {
      return false;
    }
    const BreakException = class extends Error {};
    try {
      console.group('cache', args);
      existing.edges.forEach((listing: any) => {
        const _listing = readField('node', listing);
        const _id = readStringField('id', _listing);
        const _variants = readArrayField('variants', _listing);
        if (_id === null) return;
        const incomingVariantIds = incomingNodeMap.get(_id);
        console.log(_id, '(existing)', 'in', incomingNodeMap.has(_id), '(incoming)', 'listing.id');
        if (!incomingVariantIds) return;
        const incomingVariantIdSet = new Set(incomingVariantIds);
        if (incomingVariantIdSet.size !== incomingVariantIds.length) throw BreakException;
        _variants.forEach((variant) => {
          const _variantId = readStringField('id', variant);
          if (_variantId === null) return;
          console.log(
            _variantId,
            '(existing)',
            'in',
            incomingVariantIdSet.has(_variantId),
            '(incoming)',
            'listing.variant.id',
          );
          if (!incomingVariantIdSet.has(_variantId)) throw BreakException;
        });
      });
      console.groupEnd();
    } catch (e) {
      if (e !== BreakException) {
        throw e;
      } else {
        console.warn('different ->', existing, incoming, args);
        result = false;
      }
    }
  }

  return result;
};

export const cache = new InMemoryCache({
  typePolicies: {
    ChatMessage: chatMessageTypePolicy,
    ChatRoom: {
      keyFields: ['id'], // or whatever your ID field is
      fields: {
        labels: {
          merge(_existing, incoming) {
            return incoming as unknown;
          },
        },
      },
    },
    ChatCart: {
      fields: {
        items: {
          merge(_existing, incoming) {
            return incoming as unknown;
          },
        },
      },
    },
    ChatRoomByMessage: {
      fields: {
        labels: {
          merge(_existing, incoming) {
            return incoming as unknown;
          },
        },
      },
    },
    Query: {
      fields: {
        chatStore: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        warehouses: {
          merge(_existing, incoming) {
            return incoming as unknown;
          },
        },
        orders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        profileActivities: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        products: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        variants: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        listings: {
          merge(existing: ProductListingConnection, incoming: ProductListingConnection, { args, readField }) {
            return evCompareCacheListing(existing, incoming, args, readField) ? { ...existing, ...incoming } : existing;
          },
        },
        listingBrands: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        qtyInventories: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        listingTotalSkus: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatWhatsappTemplates: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        TemplateConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatWhatsappTemplateActivity: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        TemplateActivityConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        contacts: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        contactOrders: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        contactAttributes: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatLabels: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        LabelRoom: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatAutoReplies: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatWhatsappCampaigns: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        vouchers: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        CampaignConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        stockHistoryList: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        stockInbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        stockAdjustments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        stockOutbounds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        priceAdjustments: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        priceHistories: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatWhatsAppCampaignActivity: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        CampaignActivityConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatAIToken: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        ChatAITokenConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatCustomerSurveyStats: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        ChatCustomerSurveyStatsConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatCustomerSurveys: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        ChatCustomerSurveyConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        invoices: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        paymentReceiveds: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatWhatsAppForms: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        WhatsAppFormConnection: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        contactCompanies: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatCart: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        chatReportByAgents: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        crmDeals: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        crmTickets: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        ChatKnowledges: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        ChatKnowledgeQuestions: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        orderQuotations: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        salesAgents: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        salesAgentTransactions: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        salesAgentPayouts: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } as unknown;
          },
        },
        warehouseStore: {
          merge(_existing, incoming) {
            return incoming as unknown;
          },
        },
      },
    },
    ProductWarehouse: {
      keyFields: false,
    },
    WarehouseAddress: {
      keyFields: false,
    },
    Warehouses: {
      keyFields: ['id'],
    },
    Accounts: {
      keyFields: ['id', ['name']],
    },
    WarehouseInventoryPromotion: {
      keyFields: false,
    },
    WarehouseInventoryReservedOrder: {
      keyFields: false,
    },
    Bin: {
      keyFields: false,
    },
    Channels: {
      keyFields: ['navbarId'],
    },
    InputGetVariantWarehousePayload: {
      keyFields: false,
    },
  },
});

export const client = new ApolloClient({
  link: errorLogLink.concat(forstokAuthLink).concat(splitLink),
  cache,
  queryDeduplication: false,
});

export const socket = io('wss://socket.forstok.com');
